import * as React from 'react';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { actionCreators } from './actions';
import { STATE } from 'app/constants';
import { SerializeType, SimpleFormProps } from 'app/infrastructure/simple-form/types';
import { CreateFolder } from './create-folder/create-folder';
import { savedReportTranslation, endpoint, ReportId } from './constants';
import { FIELD_TYPE, simpleFieldForPath, SimpleFormValidationConfig, withSimpleForm }
  from 'app/infrastructure/simple-form';
import { defaultSerializeMappings } from 'app/infrastructure/simple-form/serialize';
import { shouldClear } from './selectors';
import { form } from './constants';
import { CopyLink } from './copy-link/copy-link';
import { requiredValidation } from 'app/infrastructure/simple-form/validation/rules';
import { CreateReportProps, CreateReportState } from 'app/area/saved-reports/types';
import styles from './create-report.scss';
import { ButtonCallToAction, ButtonTertiary, ButtonSecondary, PromptConfirmation } from 'ui';
import { generateCopyLinkSearch, generateCopyLinkCompanyReport, 
  generateCopyLinkDashboard } from 'app/area/saved-reports/generate-copy-link';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import get from 'lodash/get';
import { FolderType } from 'app/area/case-report/case-listing/types';
import { StickyHeader } from 'app/shell/components/sticky-header/sticky-header';

type CreateReportComponentProps = DefaultContainerProps & CreateReportProps & typeof actionCreators
  & DefaultContainerProps & SimpleFormProps & React.PropsWithChildren;

const SimpleForm = simpleFieldForPath(STATE.savedReport);

class CreateReportComponent extends React.Component<CreateReportComponentProps, CreateReportState> {

  public static statePath: string = STATE.savedReport;
  public state: Readonly<CreateReportState> = {
    confirmSave: false
  };

  public componentDidMount() {
    this.props.getSearchRequiredUsers?.();
  }
  
  public componentDidUpdate(prevProps: CreateReportComponentProps) {
    if (shouldClear(prevProps, this.props)) {
      this.onCancel();
    }
    if (get(prevProps, 'formValues.folder.value') !== get(this.props, 'formValues.folder.value')) {
      this.props.clearFolderData();
      if(!!get(this.props, 'formValues.folder.value')) {
        this.props.loadFolderData(get(this.props, 'formValues.folder.value'));
      }
    }
  }

  private validationConfig: SimpleFormValidationConfig = [
    requiredValidation(form.title)
  ];

  private onSubmit = async() => {
    const { validate, serialize, saveReport, definition, translation, updateScheduleDefinitionOnSubmit} = this.props;
    const favouriteId = definition.favourite.v;
    const originalFavouritId = get(definition, 'validState.validFavourite.v');
    await updateScheduleDefinitionOnSubmit?.();
    if(!!this.props.scheduleFailedConstraints) {  //use 'this.props' here to get latest value from the store
      return;
    }
    validate(this.validationConfig, () => {
      saveReport(
        serialize(defaultSerializeMappings),
        favouriteId,
        this.props.updateReport,
        this.props.updateReport ? originalFavouritId : null,
        definition.selectedReport?.v,
        this.props.updateReport ? originalFavouritId : favouriteId
      );
    },
    translation.validation_error);
  };

  private onCancel = () => {
    this.props.closeReportCreator();
    this.props.clearAll();
  };

  private onGetUpdateConfirmation = () => {
    this.setState({confirmSave: true});
  };

  private onCancelUpdate = () => {
    this.setState({confirmSave: false});
  };

  private onCopySharing = () => {
    this.props.copySharingInformation();
  };

  private generateCopyLink = (favouriteId: number, selectedReport: number) => {
    
    if(selectedReport === ReportId.CompanyReport) {
      return generateCopyLinkCompanyReport(favouriteId);
    }

    if(selectedReport === ReportId.Dashboard) {
      return generateCopyLinkDashboard(favouriteId);
    }
    return generateCopyLinkSearch(favouriteId);
  };

  // eslint:disable-next-line: max-func-body-length
  private renderFields = () => {
    const { definition, fetching, sharedState, translation, fetchingCreateFolder, updateReport,
      children: scheduleReportPanel, reportIsDisabled, update_report_title, folderDefinition, ...rest } = this.props;
    if(!definition) {
      return null;
    }
    const validState = definition.validState;
    const selectedReport = definition.selectedReport?.v;

    const copyFolderSharing = folderDefinition && get(this.props, 'formValues.folder.value') &&
      (!!get(folderDefinition, 'validState.sharedWithUserGroups[0]') || !!get(folderDefinition, 'validState.sharedWithUsers[0]'));

    const folderType = selectedReport === ReportId.CompanyReport && FolderType.SpecialReport ||
      selectedReport === ReportId.Dashboard && FolderType.Dashboard || undefined;
    
    const textSubmit = selectedReport === ReportId.Dashboard ? translation.save_dashboard : translation.submit;
    const textUpdateReport = selectedReport === ReportId.Dashboard  ? translation.update_dashboard : translation.update_report_button;

    return <div>
      <div className="page-heading">
        <h1>{updateReport ? update_report_title || translation.update_report_title : this.props.title || translation.title}</h1>
      </div>
      <StickyHeader className={styles.stickyHeader}>
        <div className={styles.footer}>
          <ButtonTertiary onClick={this.onCancel} disabled={fetching}>
            {translation.cancel}
          </ButtonTertiary>{' '}
          <ButtonCallToAction id="form-submit" onClick={updateReport ? this.onGetUpdateConfirmation : this.onSubmit}
            disabled={fetching  || reportIsDisabled}>
            {!updateReport ? textSubmit : textUpdateReport}
          </ButtonCallToAction>
        </div>
      </StickyHeader>
      <div className="row">
        <div className="col-lg-4">
          <SimpleForm
            name={form.title}
            maxLength={254}
            type={FIELD_TYPE.TEXTAREA}
            label={{ text: selectedReport === ReportId.Dashboard ? translation.dashboard_title : translation.report_title, required: true }}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
            initialValue={updateReport ? definition.favourite.d : ''} />
        </div>
        <div className={`${styles.createFolder} col-lg-4`}>
          <SimpleForm
            name={form.folder}
            type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
            optionsEndpoint={endpoint.loadFolderOptions}
            optionsParams={{folderType, sharedWithCurrentUser: false}}
            label={{ text: translation.folder }}
            initialValue={updateReport && validState?.parentFavourite ? codeItemToStoreValue(validState?.parentFavourite) : ''}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.V_VALUE }} />
          <CreateFolder
            {...rest}
            translation={translation}
            fetching={fetchingCreateFolder}
            selectedReport={definition.selectedReport?.v} />
        </div>
        <div className="col-lg-4">
          <SimpleForm
            name={form.owners}
            type={FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY}
            optionsEndpoint={endpoint.loadOwnerOptions}
            label={{ text: translation.owner }}
            initialValue={validState?.owners ? validState.owners.map(codeItemToStoreValue) : []}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }} 
            displaySelectAll={true} />
        </div>
      </div>

      <h3>{translation.share_with}</h3>
      <hr />

      <div className="row">
        <div className="col-lg-4">
          <SimpleForm
            name={form.user_group}
            type={FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY}
            optionsEndpoint={endpoint.loadUserGroupsOptions}
            label={{ text: translation.user_group }}
            initialValue={updateReport && 
              validState?.sharedWithUserGroups ? validState?.sharedWithUserGroups.map(codeItemToStoreValue) : ''}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }} 
            displaySelectAll={true} />

          <ButtonSecondary disabled={!copyFolderSharing} onClick={this.onCopySharing}>
            {translation.copy_sharing}
          </ButtonSecondary>
        </div>
        <div className="col-lg-4">
          <SimpleForm
            name={form.users}
            type={FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY}
            optionsEndpoint={endpoint.loadUsersOptions}
            label={{ text: translation.users }}
            initialValue={updateReport && validState?.sharedWithUsers ? validState?.sharedWithUsers.map(codeItemToStoreValue) : ''}
            translation={translation}
            placeholder={translation.search_placeholder}
            serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
            searchRequired={this.props.searchRequiredUsers}/>
        </div>
        <div className="col-lg-4 col-sm-12">
          <CopyLink
            translation={translation}
            initialValue={!updateReport ?
              this.generateCopyLink(definition.favourite.v, selectedReport) :
              this.generateCopyLink(definition.validState?.validFavourite.v, selectedReport)}
            value={this.props.formValues[form.link_to_report] as string}
            notification={this.props.copyLinkNotification} 
            selectedReport={selectedReport}/>
        </div>
      </div>
      {scheduleReportPanel}
    </div>;
  };

  public render() {
    const { translation,  definition, className } = this.props;
    const { confirmSave } = this.state;

    const validFavourite = get(definition, 'validState.validFavourite', {});
    const selectedReport = get(definition, 'selectedReport.v', {});

    const confirmSaveMessage = <>
      {selectedReport === ReportId.Dashboard ? translation.update_dashboard_are_you_sure : translation.update_report_are_you_sure}
      <span className={styles.originalId}>{validFavourite.v} - {validFavourite.d}</span>
      <span className={styles.spacing}>
        {selectedReport === ReportId.Dashboard ? translation.update_dashboard_other_users : translation.update_report_other_users}
      </span>
      {translation.update_report_not_undone}
    </>;

    return <div>
      <div className={`${styles.createReport} ${className}`}>
        {this.renderFields()}
        {confirmSave && <PromptConfirmation
          title={selectedReport === ReportId.Dashboard ? translation.update_existing_dashboard : translation.update_report_title}
          description={confirmSaveMessage}
          continueText={selectedReport === ReportId.Dashboard ? translation.update_dashboard : translation.update_report_button}
          cancelText={translation.cancel}
          onCancel={this.onCancelUpdate}
          onContinue={this.onSubmit}
        /> }
      </div>

    </div>;
  }
}
const container = containerFactory<CreateReportProps>(withSimpleForm(CreateReportComponent),
  actionCreators, undefined, savedReportTranslation);

export { container as CreateReport };
